import { store } from '@/app/app-store';
import { InteractionModel, TicketModel } from '@/app/shared/models';
import { refsService as refs } from '@/app/shared/refs.service';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';

@Module({
  store,
  name: 'ticketModule',
  namespaced: true,
  dynamic: true,
  preserveState: localStorage.getItem('tracker') !== null,
})
export class TicketModule extends VuexModule {
  public ticket: TicketModel = new TicketModel();
  public ticketList: TicketModel[] = [];
  public interactionList: InteractionModel[] = [];
  public consultCampaign = false;
  public consultPersonalData = false;
  public consultContract = false;
  public consultResendBankSlip = false;
  public consultResendBRCode = false;
  public consultPaymentMethod = false;
  public consultPaymentHistory = false;
  public consultRefund = false;
  public consultRefundGroup = false;
  public consultServiceRecord = false;
  public consultWebhookEvent = false;

  public editPersonalData = false;
  public editContract = false;
  public editInstallment = false;
  public editPaymentMethod = false;
  public editResendCertificate = false;
  public editInstallmentGenerate = false;
  public editInstallmentReturn = false;
  public editRefund = false;

  @Mutation
  public SET_TICKET_LIST(ticketList: TicketModel[]) {
    this.ticketList = refs._.chain(ticketList)
      .map((ticket) => {
        return {
          ...ticket,
          Created: refs.moment
            .utc(ticket.Created)
            .local()
            .toDate(),
        };
      })
      .orderBy((ticket) => ticket.Created, ['desc'])
      .value();
  }
  @Mutation
  public SET_TICKET(ticket: TicketModel) {
    this.ticket = ticket;
  }
  @Mutation
  public SET_INTERACTION_LIST(interactionList: InteractionModel[]) {
    this.interactionList = refs._.chain(interactionList)
      .map((interaction) => {
        return {
          ...interaction,
          Created: refs.moment
            .utc(interaction.Created)
            .local()
            .toDate(),
        };
      })
      .orderBy((interaction) => interaction.Created, ['asc'])
      .value();
  }

  @Mutation
  public SET_CONSULT_PERSONAL_DATA(consultPersonalData: boolean) {
    this.consultPersonalData = consultPersonalData;
  }
  @Mutation
  public SET_CONSULT_CONTRACT(consultContract: boolean) {
    this.consultContract = consultContract;
  }
  @Mutation
  public SET_CONSULT_RESEND_BANKSLIP(consultResendBankSlip: boolean) {
    this.consultResendBankSlip = consultResendBankSlip;
  }
  @Mutation
  public SET_CONSULT_RESEND_BRCODE(consultResendBRCode: boolean) {
    this.consultResendBRCode = consultResendBRCode;
  }
  @Mutation
  public SET_CONSULT_PAYMENT_METHOD(consultPaymentMethod: boolean) {
    this.consultPaymentMethod = consultPaymentMethod;
  }
  @Mutation
  public SET_CONSULT_PAYMENT_HISTORY(consultPaymentHistory: boolean) {
    this.consultPaymentHistory = consultPaymentHistory;
  }
  @Mutation
  public SET_CONSULT_CAMPAIGN(consultCampaign: boolean) {
    this.consultCampaign = consultCampaign;
  }
  @Mutation
  public SET_CONSULT_WEBHOOK_EVENT(consultWebhookEvent: boolean) {
    this.consultWebhookEvent = consultWebhookEvent;
  }
  @Mutation
  public SET_CONSULT_REFUND(consultRefund: boolean) {
    this.consultRefund = consultRefund;
  }
  @Mutation
  public SET_EDIT_REFUND(editRefund: boolean) {
    this.editRefund = editRefund;
  }
  @Mutation
  public SET_CONSULT_REFUND_GROUP(consultRefundGroup: boolean) {
    this.consultRefundGroup = consultRefundGroup;
  }
  @Mutation
  public SET_CONSULT_SERVICE_RECORD(consultServiceRecord: boolean) {
    this.consultServiceRecord = consultServiceRecord;
  }
  @Mutation
  public SET_EDIT_PERSONAL_DATA(editPersonalData: boolean) {
    this.editPersonalData = editPersonalData;
  }
  @Mutation
  public SET_EDIT_CONTRACT(editContract: boolean) {
    this.editContract = editContract;
  }
  @Mutation
  public SET_EDIT_INSTALLMENT(editInstallment: boolean) {
    this.editInstallment = editInstallment;
  }
  @Mutation
  public SET_EDIT_PAYMENT_METHOD(editPaymentMethod: boolean) {
    this.editPaymentMethod = editPaymentMethod;
  }
  @Mutation
  public SET_EDIT_RESEND_CERTIFICATE(editResendCertificate: boolean) {
    this.editResendCertificate = editResendCertificate;
  }
  @Mutation
  public SET_EDIT_INSTALLMENT_GENERATE(editInstallmentGenerate: boolean) {
    this.editInstallmentGenerate = editInstallmentGenerate;
  }
  @Mutation
  public SET_EDIT_INSTALLMENT_RETURN(editInstallmentReturn: boolean) {
    this.editInstallmentReturn = editInstallmentReturn;
  }
  @Mutation
  public SET_CONSULT_RESET(consultReset: boolean) {
    this.consultPersonalData = consultReset;
    this.consultContract = consultReset;
    this.consultPaymentMethod = consultReset;
    this.consultPaymentHistory = consultReset;
    this.consultRefund = consultReset;
    this.consultServiceRecord = consultReset;
    this.editPersonalData = consultReset;
    this.editContract = consultReset;
    this.editPaymentMethod = consultReset;
    this.editInstallmentGenerate = consultReset;
    this.editInstallmentReturn = consultReset;
    this.consultResendBankSlip = consultReset;
  }

  @Action({ commit: 'SET_TICKET_LIST', rawError: true })
  public async loadTicketList(filter?: string) {
    return await refs.services.ticket.getTicketList(filter);
  }
  @Action({ commit: 'SET_TICKET', rawError: true })
  public async loadTicketById(ticketId: string) {
    return await refs.services.ticket.getTicketById(ticketId);
  }
  @Action({ rawError: true })
  public async createTicket(ticket: TicketModel) {
    return await refs.services.ticket.createTicket(ticket);
  }
  @Action({ rawError: true })
  public async completeTicket(ticketId: string) {
    return await refs.services.ticket.completeTicket(ticketId);
  }

  @Action({ rawError: true })
  public async closeTicket(ticketId: string) {
    return await refs.services.ticket.closeTicket(ticketId);
  }

  @Action({ commit: 'SET_INTERACTION_LIST', rawError: true })
  public async loadInteractionList(ticketId: string, filter?: string) {
    return await refs.services.ticket.getInteractionsList(ticketId, filter);
  }
  @Action({ rawError: true })
  public async createInteraction(interaction: InteractionModel) {
    await refs.services.ticket.createInteraction(interaction).catch(() => {
      //
    });
  }
  @Action({ rawError: true })
  public async createInteractionWithComplementDesc(interaction: {
    model: InteractionModel;
    complementDesc: string;
  }) {
    interaction.model.Description =
      interaction.model.Description + ' ' + interaction.complementDesc;
    await refs.services.ticket
      .createInteraction(interaction.model)
      .catch(() => {
        //
      });
  }
}
